import * as THREE from 'three'
import { Stage } from './scene/Stage.js'
import { Lighting } from './scene/Lighting.js'
import { Court } from './objects/Court.js'
import { RIM_CENTER, PRESETS, CONFIG } from './constants.js'
import { loadHoopCatalog, resolveHoop } from './hoops/hoopLibrary.js'
import { loadHoop } from './objects/hoops/glbHoop.js'
import { Net } from './objects/Net.js'
import { Ball } from './objects/Ball.js'
import { ShotCycle } from './sim/shotCycle.js'
import { initSceneControls, initConfigurator, hideLoader } from './ui/controls.js'
import { initQuoteModal } from './ui/quoteModal.js'
import { captureCourtShots } from './quote/courtShots.js'
import { loadCatalog, getGrayTexture, resolveModel, resolveColor } from './tiles/tileLibrary.js'
import { pickZonePalette } from './tiles/zonePalette.js'
import { autoZoneColorId } from './tiles/autoColors.js'
import { hoopOffsetRange, clampHoopOffset } from './hoops/hoopPosition.js'
import { decodeConfig, snapshotConfig, sanitizeSnapshot } from './config/configCodec.js'
import { toggleLine } from './config/lineToggle.js'
import { createSavedCourts, defaultStorage } from './config/savedCourts.js'
import { initSaveCta } from './ui/saveCta.js'

// ============================================================================
//  main — bootstrap + pętla renderu + spięcie konfiguratora nawierzchni.
//  Stan konfiguratora żyje tutaj; zmiany spinają kort, kamerę, oświetlenie i
//  środek rzutu. Pełna scena 3D (kosz, siatka, piłka, dzień/noc).
// ============================================================================

const app = document.getElementById('app')

const stage = new Stage(app)
const lighting = new Lighting(stage.scene)

const court = new Court()
stage.scene.add(court.group)

// Środek obręczy jest stały (regulaminowy RIM_CENTER) — Net i ShotCycle
// powstają raz; model kosza podmienia rebuildHoop() bez ich dotykania.
const rimCenter = new THREE.Vector3(RIM_CENTER.x, RIM_CENTER.y, RIM_CENTER.z)

const net = new Net(rimCenter)
stage.scene.add(net.mesh)

const ball = new Ball()
stage.scene.add(ball.mesh)

const cycle = new ShotCycle(ball, net, rimCenter)

const maxAniso = stage.renderer.capabilities.getMaxAnisotropy()

// --- Stan konfiguratora (inicjowany z tiles.json#default) ---
const state = {
  model: 'model-a',
  hoop: null,
  hoopOffsetTiles: 0, // pozycja kosza: 0 = za linią końcową; ± = suwak (1 szt/krok)
  widthTiles: 27,
  lengthTiles: 27,
  runoffTiles: 3,
  lineW: 27 * 0.301,   // obrys w metrach (placeholder; ustawiany z modelu w init)
  lineL: 27 * 0.301,
  lineSpec: 'compact', // 'compact' (przydomowy/ręczny) | 'fiba' (standard)
  preset: 'custom',    // id presetu lub 'custom' (tryb ręczny)
  colors: { court: 'brick-red', key: 'brick-red', runoff: 'navy', edge: 'navy', lines: 'white', linesThree: 'white', linesJr: 'red' },
  // Automatyczne kolory stref przy zmianie rozmiaru/modelu: wybieg niebieski,
  // trumna czerwona, kort czerwony (w presecie „Mały" niebieski). Flaga strefy
  // gaśnie na zawsze, gdy użytkownik sam wybierze jej kolor lub wczyta zapis.
  colorAuto: { court: true, key: true, runoff: true },
  // Widoczność linii — NIEZALEŻNA od presetu (applyPreset/applyCustom jej nie dotykają).
  // Domyślnie: senior i TICKI on (klient maluje znaczniki w 99% realizacji —
  // uwaga 25.08.2026; wyłączalne w sekcji „Linie"); junior / no-charge off.
  lines: { key: true, ftHalf: true, ftCircle: false, ticks: true, three: true, threeJr: false, noCharge: false },
  view: '3d',
}

// --- UI: kontrolki sceny (2D/3D, dzień/noc, blokada obrotu) — jeden kontroler
// ze wspólnym stanem steruje pigułkami (desktop) i kwadratami on/off (mobile). ---
initSceneControls({
  state,
  onViewChange: (view) => {
    stage.setView(view)
    court.setView(view)       // 2D: siatka kafli + płaskie kolory, bez cieni
    ball.mesh.visible = view === '3d' // piłka tylko w 3D
  },
  onToggle: (mode) => lighting.setMode(mode),
  onLock: (locked) => stage.setRotationLock(locked),
})

let catalog = null
let tileMm = 301

// Kolor wg palety STREFY: strefy płytek → paleta bieżącego modelu, strefy linii
// → wspólna paleta 'court-lines' (pickZonePalette). Paleta liczona na żywo —
// kilka wpisów, koszt pomijalny; zawsze odzwierciedla aktualny zakres barw.
const resolve = (zone, id) => resolveColor(pickZonePalette(catalog, state.model, zone), id)

// Po zmianie modelu dopasuj kolory stref do nowego zakresu: zachowaj id, które
// istnieje w palecie strefy (np. wspólne navy/light-grey; linie mają wspólną
// paletę, więc przechodzą zawsze), a brakujące zejdź do defaultColors modelu.
function reconcileColors() {
  const model = resolveModel(catalog, state.model)
  Object.keys(state.colors).forEach((zone) => {
    const palette = pickZonePalette(catalog, state.model, zone)
    if (!palette.some((p) => p.id === state.colors[zone])) state.colors[zone] = model.defaultColors[zone]
  })
}

// Wymiar płytki w metrach dla bieżącego modelu (catalog gwarantowany w czasie
// wywołania — helpery wołane dopiero po loadCatalog).
const currentTileM = () => resolveModel(catalog, state.model).size.width / 1000

// Automatyczne kolory stref (uwaga klienta 25.08.2026): przy zmianie rozmiaru
// lub modelu wybieg wraca do niebieskiego, trumna do czerwieni, kort do
// czerwieni — a w presecie „Mały" kort do niebieskiego. Kort w palecie bez
// czerwieni (E630) schodzi do jasnoniebieskiego (kolejny rodzaj z listy).
// Strefa z zgaszoną flagą colorAuto (ręczny wybór użytkownika / wczytany
// zapis) zostaje bez zmian; tak samo gdy żaden rodzaj z listy nie pasuje.
function applyAutoColors() {
  const kinds = {
    court: state.preset === 'small' ? ['blue'] : ['red', 'light-blue'],
    key: ['red'],
    runoff: ['blue'],
  }
  Object.entries(kinds).forEach(([zone, prefs]) => {
    if (!state.colorAuto[zone]) return
    for (const kind of prefs) {
      const id = autoZoneColorId(pickZonePalette(catalog, state.model, zone), kind)
      if (id) { state.colors[zone] = id; break }
    }
  })
}

// Tryb ręczny / przydomowy: obrys = krawędź płyty, linie compact, preset 'custom'.
function applyCustom({ widthTiles, lengthTiles, runoffTiles }) {
  state.widthTiles = widthTiles
  state.lengthTiles = lengthTiles
  // Wybieg przenosi się 1:1 — zmiana wymiarów NIE podbija go z 0 (wyjście z
  // „Małego" na tryb ręczny zachowuje boisko bez wybiegu). Clamp tylko na śmieci.
  state.runoffTiles = Math.min(CONFIG.RUNOFF.max, Math.max(CONFIG.RUNOFF.min, runoffTiles))
  const tileM = currentTileM()
  state.lineW = state.widthTiles * tileM
  state.lineL = state.lengthTiles * tileM
  state.lineSpec = 'compact'
  state.preset = 'custom'
}

// Preset: liczba płytek stała (tilesW/tilesL — presety FIBA) albo liczona z celu
// w metrach przez ceil (przydomowe; płyta ≥ standard). Wybór presetu z UI ZAWSZE
// przywraca wybieg do domyślnego dla presetu (runoffDefault — „Mały" startuje
// z 0, reszta z globalnych 3 kafelków); `overrides` służy odtwarzaniu
// zapisu/linku — przywraca zapisany wybieg (clamp do globalnych granic) i (gdy
// preset na to pozwala, maxCourtLenM) wyregulowaną długość.
function applyPreset(p, overrides = {}) {
  const tileM = currentTileM()
  state.widthTiles = p.tilesW ?? evenCeilTiles(p.targetW, tileM)
  state.lengthTiles = p.tilesL ?? Math.ceil(p.targetL / tileM)
  if (p.maxCourtLenM && Number.isInteger(overrides.lengthTiles)) {
    state.lengthTiles = clampPresetLength(p, overrides.lengthTiles)
  }
  state.runoffTiles = Number.isInteger(overrides.runoffTiles)
    ? Math.min(CONFIG.RUNOFF.max, Math.max(CONFIG.RUNOFF.min, overrides.runoffTiles))
    : (p.runoffDefault ?? CONFIG.RUNOFF.default)
  // Preset przywraca kosz do pozycji standardowej (0); override — z odtwarzania
  // zapisu. Clamp do zakresu robi rebuildSurface.
  state.hoopOffsetTiles = Number.isInteger(overrides.hoopOffsetTiles) ? overrides.hoopOffsetTiles : 0
  // Obrys na krawędzi siatki płytek (do końca kratki końcowej) — wokół widoczny
  // wybieg, na obwodzie obrzeże. Dotyczy też FIBA: linia sięga końca płytek, a
  // regulaminowe oznaczenia wewnętrzne (łuk 6,75 m, trumna 4,90 m…) daje lineSpec='fiba'.
  state.lineW = state.widthTiles * tileM
  state.lineL = state.lengthTiles * tileM
  state.lineSpec = p.spec
  state.preset = p.id
}

// Szerokość kortu z celu w metrach: ceil do pełnych płytek, potem w górę do
// PARZYSTEJ liczby. Trumna FIBA (16 szt) musi trafiać bokami w spoiny, a na
// nieparzystej szerokości środek siatki wypada w połowie płytki i trumna
// spadałaby do 15 szt. Płyta zawsze ≥ standard (nadmiar < 2 płytki).
function evenCeilTiles(targetM, tileM) {
  let t = Math.ceil(targetM / tileM)
  if (t % 2) t += 1
  return t
}

// Zakres długości pola gry w presecie z regulowaną długością (5×5): min z
// globalnych granic, max = maxCourtLenM przeliczone na płytki (podłoga — pole
// gry nie może przekroczyć 28 m).
function clampPresetLength(p, tiles) {
  const maxTiles = Math.floor(p.maxCourtLenM / currentTileM())
  return Math.min(maxTiles, Math.max(CONFIG.LENGTH.min, tiles))
}

// Definicja bieżącego kosza z katalogu (null przed wczytaniem hoops.json) —
// źródło pozycji obręczy (rimFromBaseline).
const currentHoopDef = () => (hoopCatalog ? resolveHoop(hoopCatalog, state.hoop) : null)

// Pełna przebudowa nawierzchni (model/wymiary). Tekstura szara jest cache'owana
// per model, więc zmiana tylko wymiarów też przechodzi przez tę ścieżkę tanio.
async function rebuildSurface() {
  const model = resolveModel(catalog, state.model)
  tileMm = model.size.width
  const grayTex = await getGrayTexture(model, maxAniso)
  const hoopDef = currentHoopDef()
  // Suwak pozycji kosza: offset przycinany do zakresu bieżącego boiska/kosza
  // (zmiana długości boiska lub kosza może zawęzić zakres) — clamp TU, bo tędy
  // przechodzi każda przebudowa.
  const baseRim = hoopDef?.rimFromBaseline ?? RIM_CENTER.z
  state.hoopOffsetTiles = clampHoopOffset(
    state.hoopOffsetTiles,
    hoopOffsetRange({ lengthTiles: state.lengthTiles, tileM: currentTileM(), rimFromBaseline: baseRim }),
  )
  const hoopOffM = state.hoopOffsetTiles * currentTileM()
  court.rebuild({
    grayTex,
    tileMm,
    widthTiles: state.widthTiles,
    lengthTiles: state.lengthTiles,
    runoffTiles: state.runoffTiles,
    lineW: state.lineW,
    lineL: state.lineL,
    lineSpec: state.lineSpec,
    lines: state.lines,
    edge: model.edge ?? 80,
    tileThickMm: model.size.thickness,
    // Pozycja obręczy: baza per MODEL kosza + suwak. Zaplecze za linią końcową
    // = dokładnie wybieg (bez minimum na maszt) — słup może stać poza płytą.
    rimZ: baseRim + hoopOffM,
    hoopOffsetM: hoopOffM,
  })
  court.recolor(state.colors, resolve)
  // Dopasowanie linii za 3 pkt (pełnowymiarowa/umowna) policzone przy budowie
  // linii — do payloadu wyceny (state) i badge'a w sekcji „Linie" (ui).
  state.lineFit = court.lineFit
  ui?.updateLineBadges?.(court.lineFit)
  ui?.updateHoopSlider?.() // zakres/odczyt suwaka pozycji kosza (offset mógł być przycięty)
  // Sidebar dosuwany do stanu po każdej przebudowie — automat koloru trumny i
  // wyłączenie linii juniorskiej w „Małym" zmieniają stan poza sekcjami UI.
  ui?.refreshColors?.()
  ui?.syncLines?.()
  // Scena za suwakiem pozycji kosza — TU, bo offset zmienia się (albo jest
  // przycinany) wyłącznie na ścieżkach przechodzących przez rebuildSurface.
  applyHoopOffset()
  applyDimensions()
}

// Pozycja kosza w scenie 3D: model, obręcz (rimCenter), siatka i cel rzutu idą
// za suwakiem. Wołane po każdej zmianie offsetu i po podmianie modelu kosza.
// hoopBaseZ = pozycja Z grupy z transformu hoops.json (offset 0).
let hoopBaseZ = 0
function applyHoopOffset() {
  const def = currentHoopDef()
  const offM = state.hoopOffsetTiles * currentTileM()
  if (hoopInstance) hoopInstance.group.position.z = hoopBaseZ + offM
  const rimZ = (def?.rimFromBaseline ?? RIM_CENTER.z) + offM
  if (Math.abs(rimCenter.z - rimZ) > 1e-9) {
    rimCenter.z = rimZ
    net.setRimCenter(rimCenter)
    cycle.setRim(rimCenter)
  }
}

// Po zmianie wymiarów: środek rzutu, kamera cieni i auto-kadr.
// Auto-kadr (ustawienie kamery 3D) tylko PRZY PIERWSZEJ budowie — kolejne zmiany
// parametrów boiska NIE resetują kamery 3D (zachowujemy obrót/zoom użytkownika);
// aktualizujemy jedynie granice (cienie) i dopasowanie kadru 2D.
let framed = false
function applyDimensions() {
  const courtL = (state.lengthTiles * tileMm) / 1000
  cycle.setCenter(courtL / 2)
  lighting.updateBounds(court.bounds)
  if (!framed) {
    stage.frameCourt(court.bounds)
    framed = true
  } else {
    stage.refit(court.bounds)
  }
}

let hoopCatalog = null
let hoopInstance = null

const savedCourts = createSavedCourts(defaultStorage())
let ui = null      // zwrot initConfigurator ({ renderSaved, setActive })
let saveCta = null // zwrot initSaveCta ({ refresh })

// Podmiana kosza: dispose() starego, synchroniczna budowa nowego z katalogu.
// Nieznane id łapie fallback resolveHoop (pierwszy kosz z katalogu).
// Guard: bez wczytanego katalogu nie ma czego budować (UI nie powinno wtedy
// wołać onHoopChange, ale kontrakt funkcji nie polega na dyscyplinie UI).
// Podmiana kosza: asynchroniczne wczytanie modelu z katalogu. Stary model
// zwalniamy DOPIERO po wczytaniu nowego (brak luki/migotania). Stale-guard:
// jeśli w trakcie ładowania wybrano inny kosz, świeży model zwalniamy i porzucamy.
async function rebuildHoop() {
  if (!hoopCatalog) return
  const def = resolveHoop(hoopCatalog, state.hoop)
  const reqId = def.id
  const next = await loadHoop(def)
  if (reqId !== resolveHoop(hoopCatalog, state.hoop).id) {
    next.dispose()
    return
  }
  if (hoopInstance) {
    stage.scene.remove(hoopInstance.group)
    hoopInstance.dispose()
  }
  hoopInstance = next
  stage.scene.add(hoopInstance.group)

  // Pozycja obręczy = baza per MODEL kosza (rimFromBaseline) + suwak pozycji.
  // Zmiana przesuwa model, siatkę, cel rzutu i linie liczone od obręczy
  // (no-charge, łuki za 3 pkt) — gdy obręcz się przesunęła, przebuduj nawierzchnię.
  hoopBaseZ = hoopInstance.group.position.z
  const prevRimZ = rimCenter.z
  applyHoopOffset()
  if (Math.abs(rimCenter.z - prevRimZ) > 1e-9) {
    await rebuildSurface()
  }
}

;(async () => {
  try {
    // Katalog koszy ładowany równolegle; jego brak nie blokuje nawierzchni
    // (console.error i scena działa bez kosza).
    const [tileCatalog, hoopCat] = await Promise.all([
      loadCatalog(),
      loadHoopCatalog().catch((err) => { console.error('Katalog koszy:', err); return null }),
    ])
    catalog = tileCatalog

    // Konfiguracja z linku #cfg= (wycena/zapis) ma pierwszeństwo przed
    // domyślną z tiles.json#default. Śmieci w hashu → null → normalny start.
    const cfgMatch = location.hash.match(/cfg=([A-Za-z0-9_-]+)/)
    const startCfg = cfgMatch ? decodeConfig(cfgMatch[1], { catalog, hoopCatalog: hoopCat }) : null

    if (startCfg) {
      state.model = startCfg.model
      state.colors = startCfg.colors
      state.lines = startCfg.lines
      // Kolory z linku to świadomy wybór autora konfiguracji — automat stref off.
      state.colorAuto = { court: false, key: false, runoff: false }
      const p = startCfg.preset !== 'custom' ? PRESETS.find((x) => x.id === startCfg.preset) : null
      // Preset z override'ami: wybieg (niezależny od presetu), wyregulowana
      // długość (preset 5×5) i pozycja kosza wracają z zapisu.
      if (p) {
        applyPreset(p, {
          lengthTiles: startCfg.lengthTiles,
          runoffTiles: startCfg.runoffTiles,
          hoopOffsetTiles: startCfg.hoopOffsetTiles,
        })
      } else {
        applyCustom({
          widthTiles: startCfg.widthTiles,
          lengthTiles: startCfg.lengthTiles,
          runoffTiles: startCfg.runoffTiles,
        })
        state.hoopOffsetTiles = startCfg.hoopOffsetTiles ?? 0
      }
    } else {
      const d = catalog.default
      state.model = d.model
      // Kolory startowe = defaultColors modelu startowego (wszystkie strefy łącznie
      // z linesJr). Zakres barw jest per model, więc start bierze komplet z modelu.
      state.colors = { ...resolveModel(catalog, state.model).defaultColors }
      // Domyślny preset z katalogu (np. 'medium') aktywny od startu; wybieg niezależny
      // od presetu. Brak/nieznany preset ⇒ tryb „Własny" z domyślnych płytek.
      const startPreset = d.preset ? PRESETS.find((p) => p.id === d.preset) : null
      if (startPreset) {
        applyPreset(startPreset) // wybieg ustawiany na domyślny presetu (runoffDefault)
      } else {
        applyCustom({ widthTiles: d.widthTiles, lengthTiles: d.lengthTiles, runoffTiles: d.runoffTiles })
      }
    }

    await rebuildSurface()

    // Kosz budowany PO nawierzchni i we własnym try — błąd buildera (błąd
    // programisty) nie może odebrać użytkownikowi nawierzchni ani UI.
    if (hoopCat) {
      hoopCatalog = hoopCat
      state.hoop = startCfg?.hoop ?? hoopCatalog.default
      try {
        await rebuildHoop()
      } catch (err) {
        console.error('Budowa kosza:', err)
      }
    }

    // Sidebar budowany funkcją, bo wczytanie zapisu przebudowuje go w całości
    // (initConfigurator stawia DOM przez innerHTML — wywołanie jest idempotentne).
    function buildSidebar(initialSection) {
      ui = initConfigurator({
        catalog,
        hoopCatalog,
        state,
        savedCourts,
        initialSection,
        // Zapis z localStorage przechodzi tę samą walidację co link #cfg= —
        // mógł powstać przed zmianą tiles.json (nieistniejący model, kolory
        // spoza palety, popsute wymiary). Snapshot nie do uratowania → klik
        // po cichu ignorowany (konwencja cichej degradacji w tym module).
        onLoadSaved: (cfg) => {
          const clean = sanitizeSnapshot(cfg, { catalog, hoopCatalog })
          if (clean) applyConfig(clean)
        },
        onSavedChange: () => saveCta?.refresh(),
        onModelChange: async (modelId) => {
          state.model = modelId
          // Zakres barw jest per model — dopasuj kolory stref do nowej palety przed
          // przebudową (rebuildSurface woła recolor z resolve wg nowego modelu).
          reconcileColors()
          // Zmiana modelu przelicza płytki: preset standardowy zachowuje cel w metrach
          // (ceil), tryb ręczny zachowuje liczbę płytek (obrys = płyta).
          if (state.preset === 'custom') {
            applyCustom({
              widthTiles: state.widthTiles,
              lengthTiles: state.lengthTiles,
              runoffTiles: state.runoffTiles,
            })
          } else {
            const p = PRESETS.find((x) => x.id === state.preset)
            if (p) applyPreset(p)
          }
          applyAutoColors() // automat trzyma kolory stref także w palecie nowego modelu
          await rebuildSurface()
        },
        onSizeChange: async (sizes) => {
          applyCustom(sizes)
          applyAutoColors() // ręczny wymiar = „inny rozmiar" → domyślne kolory stref
          await rebuildSurface()
        },
        // Wybieg jest niezależny od presetu — zmieniamy TYLKO liczbę płytek
        // wybiegu i przebudowujemy płytę; preset/lineSpec/obrys zostają.
        onRunoffChange: async (runoffTiles) => {
          state.runoffTiles = runoffTiles
          await rebuildSurface()
        },
        // Regulacja długości WEWNĄTRZ presetu (5×5, maxCourtLenM): boisko może
        // być niewymiarowe w długości, ale zostaje w presecie — linie FIBA i
        // stała szerokość 50 szt nie znikają. Obrys idzie za krawędzią siatki.
        onPresetLengthChange: async (lengthTiles) => {
          const p = PRESETS.find((x) => x.id === state.preset)
          if (!p || !p.maxCourtLenM) return
          state.lengthTiles = clampPresetLength(p, lengthTiles)
          state.lineL = state.lengthTiles * currentTileM()
          await rebuildSurface()
        },
        onPreset: async (presetId) => {
          const p = PRESETS.find((x) => x.id === presetId)
          if (!p) return
          applyPreset(p)
          // „Mały": wyłącz linię juniorską (za ciasno na dwa łuki — uwaga
          // klienta 25.08.2026); użytkownik może ją z powrotem włączyć ręcznie.
          if (p.id === 'small') state.lines.threeJr = false
          applyAutoColors()
          await rebuildSurface()
        },
        onColorChange: (zone, id) => {
          state.colors[zone] = id
          // Ręczny wybór koloru strefy wygrywa z jej automatem na stałe.
          if (zone in state.colorAuto) state.colorAuto[zone] = false
          court.recolor(state.colors, resolve)
        },
        onLineToggle: async (key, value) => {
          // Półkole/koło RW wykluczają się (LINE_DEFS.excludes): włączenie
          // jednego gasi drugie; checkboxy dosuwa syncLines po przebudowie.
          state.lines = toggleLine(state.lines, key, value)
          await rebuildSurface()
        },
        onHoopChange: (hoopId) => {
          state.hoop = hoopId
          rebuildHoop().catch((err) => console.error('Budowa kosza:', err))
        },
        // Suwak pozycji kosza (krok = 1 płytka): rebuildSurface przycina offset,
        // przelicza trumnę/linie za obręczą i dosuwa scenę (model/siatka/rzut).
        onHoopOffsetChange: async (tiles) => {
          state.hoopOffsetTiles = tiles
          await rebuildSurface()
        },
      })
    }
    buildSidebar()

    // Wczytanie zapisu: podmiana stanu → przebudowa sceny → przebudowa sidebara
    // z otwartą sekcją „Zapisane boiska" (użytkownik nie traci kontekstu listy).
    async function applyConfig(cfg) {
      state.model = cfg.model
      state.colors = cfg.colors
      state.lines = cfg.lines
      // Kolory z zapisu to świadomy wybór użytkownika — automat stref off.
      state.colorAuto = { court: false, key: false, runoff: false }
      const p = cfg.preset !== 'custom' ? PRESETS.find((x) => x.id === cfg.preset) : null
      // Te same override'y co przy starcie z #cfg= — wybieg, długość 5×5 i
      // pozycja kosza z zapisu.
      if (p) {
        applyPreset(p, { lengthTiles: cfg.lengthTiles, runoffTiles: cfg.runoffTiles, hoopOffsetTiles: cfg.hoopOffsetTiles })
      } else {
        applyCustom({ widthTiles: cfg.widthTiles, lengthTiles: cfg.lengthTiles, runoffTiles: cfg.runoffTiles })
        state.hoopOffsetTiles = cfg.hoopOffsetTiles ?? 0
      }
      await rebuildSurface() // dosuwa też kosz/siatkę/cel do odtworzonej pozycji
      if (hoopCatalog && cfg.hoop && cfg.hoop !== state.hoop) {
        state.hoop = cfg.hoop
        rebuildHoop().catch((err) => console.error('Budowa kosza:', err))
      }
      buildSidebar('saved')
    }

    // CTA inicjowane RAZ — przyciski żyją poza #cfg-panel, więc przebudowa
    // sidebara ich nie dotyka (ponowne initSaveCta dublowałoby listenery).
    saveCta = initSaveCta({
      state,
      savedCourts,
      onSaved: () => ui?.renderSaved(),
      onOpenSaved: () => {
        ui?.setActive('saved')
        document.querySelector('.sec[data-sec="saved"]')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      },
    })

    // Modal wyceny: czyta state/catalog/hoopCatalog przez referencję → przy
    // wysyłce ma zawsze aktualną konfigurację boiska. captureShots renderuje
    // 4 zrzuty PNG aktualnej sceny (3× 3D + rzut 2D z wymiarami) do payloadu.
    const captureShots = () =>
      captureCourtShots({ stage, court, ball, lighting, getHoopGroup: () => hoopInstance?.group ?? null })
    initQuoteModal({
      state,
      catalog,
      hoopCatalog,
      captureShots,
      onQuoteSent: () => {
        savedCourts.save(snapshotConfig(state))
        ui?.renderSaved()
        saveCta?.refresh()
      },
    })
    // Dev: hak do podglądu zrzutów wyceny bez wysyłki (skrypty CDP, konsola).
    // Vite wycina to z builda produkcyjnego.
    if (import.meta.env.DEV) window.__captureCourtShots = captureShots

    // Dev-nakładka strojenia pozycji modelu (lazy — tylko przy #align).
    // Dopasowanie z granicami, nie includes: ciąg „align" mógłby trafić się
    // WEWNĄTRZ base64url payloadu #cfg= i włączyć nakładkę przypadkiem.
    // Łapie: '#align', '#align&night', '#cfg=…&align'.
    if (/(^#|[#&])align($|[=&])/.test(location.hash)) {
      const { initHoopAlign } = await import('./ui/hoopAlign.js')
      initHoopAlign({ stage, getHoop: () => hoopInstance, rimCenter })
    }

    hideLoader() // model gotowy — zdejmij wskaźnik ładowania
  } catch (err) {
    console.error('Konfigurator nawierzchni:', err)
    hideLoader() // nawet przy błędzie zdejmij wskaźnik (nie blokuj UI)
  }
})()

// Rozłożenie wartości trybu (mix: 0 dzień, 1 noc) na zależne moduły.
const bgNight = document.getElementById('bg-night')

function applyMix(m) {
  stage.setExposure(lighting.exposure)
  if (bgNight) bgNight.style.opacity = m // ciemne tło narasta wraz z nocą
}

// Tryb startowy można wymusić adresem (#night). Dopasowanie z granicami, nie
// includes — ta sama kolizja co przy #align: ciąg „night" mógłby trafić się
// WEWNĄTRZ base64url payloadu #cfg= i wystartować scenę w nocy bez życzenia
// użytkownika. Łapie: '#night', '#night&align', '#cfg=…&night'.
const startMix = /(^#|[#&])night($|[=&])/.test(location.hash) ? 1 : 0
lighting.forceMix(startMix)
applyMix(startMix)

// Pierwsza klatka synchronicznie (pewny obraz pod wskaźnikiem ładowania).
stage.update(0)
net.update(0)
stage.render()

// --- Pętla renderu ---
const clock = new THREE.Clock()

function animate() {
  requestAnimationFrame(animate)
  const dt = Math.min(clock.getDelta(), 0.05)

  lighting.update(dt)
  applyMix(lighting.mix)

  // W 2D piłka jest ukryta, więc cykl rzutu i siatkę wstrzymujemy (wracają w 3D).
  if (stage.view === '3d') {
    cycle.update(dt)
    net.update(dt)
  }
  stage.update(dt)

  // Siła faktury kafelków wg zoomu. Dystans liczy Stage per widok: 3D = odległość kamery
  // do celu orbity; 2D orto = zoom przeliczony na ekwiwalentny dystans (oddalenie gasi fakturę).
  court.setTextureStrength(stage.textureCameraDistance())

  stage.render()
}
animate()
